import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { authAxios } from "../../utils/session_utils";
import { AutopodderResultRow, PreSeedEntry, ScheduleDataItem } from "../../batch_jobs/types";

interface PreSeedData {
    slots: AutopodderResultRow[];
    schedules: { [key: string]: ScheduleDataItem };
}

const usePreSeedingHook = (batchJobId: string | null) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [slots, setSlots] = useState<AutopodderResultRow[]>([]);
    const [scheduleData, setScheduleData] = useState<{ [key: string]: ScheduleDataItem }>({});
    const [slotSelectionDict, setSlotSelectionDict] = useState<{ [key: string]: string[] }>({});
    const [slotPositionDict, setSlotPositionDict] = useState<{ [slotId: string]: { [adName: string]: 'start' | 'end' } }>({});
    const [redirectToBatchJobs, setRedirectToBatchJobs] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch pre-seed data from backend
    useEffect(() => {
        const fetchPreSeedData = async () => {
            if (!batchJobId) return;

            try {
                setLoading(true);
                setError(null);
                const url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/preseed-data?batchJobId=${encodeURIComponent(batchJobId)}`;
                const config = {
                    method: "GET" as const,
                    url,
                    headers: { "Content-Type": "application/json" },
                };
                const response = await authAxios(config);
                const data = response.data as PreSeedData;

                setSlots(data.slots || []);
                setScheduleData(data.schedules || {});

                // Initialize empty selections for each slot
                const initialDict: { [key: string]: string[] } = {};
                (data.slots || []).forEach((slot: AutopodderResultRow) => {
                    initialDict[slot.ID] = [];
                });
                setSlotSelectionDict(initialDict);
            } catch (err: any) {
                console.error("Error fetching pre-seed data:", err);
                setError(err?.response?.data?.error || err.message || "Failed to load pre-seed data");
            } finally {
                setLoading(false);
            }
        };

        fetchPreSeedData();
    }, [batchJobId]);

    // In preseed mode, original counts are always 0 (no previous autopodder selections)
    const getOriginalAdSelectionCounts = useCallback((): { [scheduleName: string]: number } => {
        return {};
    }, []);

    // Submit pre-seed assignments and trigger autopodder
    const handleSubmitPreSeed = useCallback(async () => {
        if (!batchJobId) return;

        try {
            setSubmitting(true);
            // Build pre-seed assignments with position info: { slotId: PreSeedEntry[] }
            const preSeedAssignments: { [slotId: string]: PreSeedEntry[] } = {};
            for (const [slotId, adNames] of Object.entries(slotSelectionDict)) {
                if (adNames.length > 0) {
                    preSeedAssignments[slotId] = adNames.map(adName => ({
                        adName,
                        position: slotPositionDict[slotId]?.[adName] || 'start',
                    }));
                }
            }

            const url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/submit-preseed`;
            const config = {
                method: "POST" as const,
                url,
                headers: { "Content-Type": "application/json" },
                data: { batchJobId, preSeedAssignments },
            };
            const response = await authAxios(config);
            if (response.data?.error) {
                message.error("Error submitting pre-seeds: " + response.data.error);
            } else {
                message.success("Pre-seeds submitted. AutoPodder job started.");
                setRedirectToBatchJobs(true);
            }
        } catch (err: any) {
            console.error("Error submitting pre-seed:", err);
            message.error("Failed to submit pre-seeds");
        } finally {
            setSubmitting(false);
        }
    }, [batchJobId, slotSelectionDict, slotPositionDict]);

    // Skip pre-seeding: submit with empty assignments
    const handleSkipPreSeed = useCallback(async () => {
        if (!batchJobId) return;

        try {
            setSubmitting(true);
            const url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/submit-preseed`;
            const config = {
                method: "POST" as const,
                url,
                headers: { "Content-Type": "application/json" },
                data: { batchJobId, preSeedAssignments: {} },
            };
            const response = await authAxios(config);
            if (response.data?.error) {
                message.error("Error skipping pre-seed: " + response.data.error);
            } else {
                message.success("AutoPodder job started (no pre-seeds).");
                setRedirectToBatchJobs(true);
            }
        } catch (err: any) {
            console.error("Error skipping pre-seed:", err);
            message.error("Failed to skip pre-seeding");
        } finally {
            setSubmitting(false);
        }
    }, [batchJobId]);

    return {
        loading,
        submitting,
        slots,
        scheduleData,
        slotSelectionDict,
        setSlotSelectionDict,
        slotPositionDict,
        setSlotPositionDict,
        redirectToBatchJobs,
        error,
        getOriginalAdSelectionCounts,
        handleSubmitPreSeed,
        handleSkipPreSeed,
    };
};

export default usePreSeedingHook;
